import { useCallback, useEffect, useState } from 'react';

import { App, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import Layout from 'components/Layout/Layout';

import { ApiErrorData } from 'models/ApiResponse';
import {
  OperationRequest,
  OperationRequestCounts,
  OperationRequestKpis,
  OperationRequestListResponse,
  OperationRequestTab,
} from 'models/OperationRequest';

import { OperationRequirementService } from 'services/OperationRequirementService';

import CancelRequestModal from './components/CancelRequestModal';
import OpsFilters from './components/OpsFilters';
import OpsKpiCards from './components/OpsKpiCards';
import OpsRequestTable from './components/OpsRequestTable';

import './RequirementsOperation.scss';

const PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];
const PAGE_SIZE_STORAGE_KEY = 'requirementsOperation.pageSize';

const EMPTY_COUNTS: OperationRequestCounts = {
  todas: 0,
  borrador: 0,
  pendiente: 0,
  'en-curso': 0,
  riesgo: 0,
  publicado: 0,
  listo: 0,
  cancelado: 0,
};

const EMPTY_KPIS: OperationRequestKpis = {
  solicitudes: 0,
  slots: 0,
  filled: 0,
  urgentes: 0,
};

const getInitialPageSize = (): number => {
  try {
    const stored = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (PAGE_SIZE_OPTIONS.includes(parsed)) return parsed;
    }
  } catch {
    // localStorage puede no estar disponible
  }
  return PAGE_SIZE;
};

function RequirementsOperation() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const i18nBase = 'scenes.requirements_operation.scene';

  const [loading, setLoading] = useState(true);
  const [term, setTerm] = useState('');
  const [tab, setTab] = useState<OperationRequestTab>('todas');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(getInitialPageSize);
  const [total, setTotal] = useState(0);
  const [dataSource, setDataSource] = useState<OperationRequest[]>([]);
  const [counts, setCounts] = useState<OperationRequestCounts>(EMPTY_COUNTS);
  const [kpis, setKpis] = useState<OperationRequestKpis>(EMPTY_KPIS);

  const [cancelling, setCancelling] = useState<OperationRequest | null>(null);
  const fetchList = useCallback(async () => {
    setLoading(true);
    const response = await OperationRequirementService.list({
      term,
      tab,
      page,
      pageSize,
    });
    setLoading(false);

    if (response.success) {
      const data = response.data as OperationRequestListResponse;
      setDataSource(data.items);
      setCounts(data.counts);
      setKpis(data.kpis);
      setTotal(data.total);
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.fetch_list`),
        3.5
      );
    }
  }, [term, tab, page, pageSize, message, t, i18nBase]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const handleTermChange = (value: string) => {
    setTerm(value);
    setPage(1);
  };

  const handleTabChange = (value: OperationRequestTab) => {
    setTab(value);
    setPage(1);
  };

  const handlePaginationChange = (nextPage: number, nextPageSize: number) => {
    if (nextPageSize !== pageSize) {
      setPageSize(nextPageSize);
      setPage(1);
      try {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(nextPageSize));
      } catch {
        // ignore
      }
      return;
    }
    setPage(nextPage);
  };

  const handleView = (row: OperationRequest) => {
    if (row.status === 'borrador') {
      navigate(`/portal-go/requirements-review/new?draftId=${row.id}`);
      return;
    }
    navigate(`/portal-go/requirements-review/${row.id}`);
  };

  const handleContinueDraft = (row: OperationRequest) => {
    navigate(`/portal-go/requirements-review/new?draftId=${row.id}`);
  };

  const handleEdit = (row: OperationRequest) => {
    navigate(`/portal-go/requirements-review/new?editId=${row.id}`);
  };

  const handleAssign = (row: OperationRequest) => {
    navigate(`/portal-go/requirements-review/${row.id}/assign`);
  };

  const handleDeleteDraft = async (row: OperationRequest) => {
    const response = await OperationRequirementService.deleteDraft(row.id);
    if (response.success) {
      message.success(t(`${i18nBase}.messages.draft_deleted`), 3.5);
      fetchList();
    } else {
      const error = response.data as ApiErrorData;
      message.error(
        (error.message as string) || t(`${i18nBase}.errors.delete_draft`),
        3.5
      );
    }
  };

  return (
    <Layout title="">
      <div className="requirements-operation">
        <OpsFilters
          term={term}
          tab={tab}
          counts={counts}
          loading={loading}
          onTermChange={handleTermChange}
          onTabChange={handleTabChange}
          onCreate={() => navigate('/portal-go/requirements-review/new')}
        />

        <OpsKpiCards kpis={kpis} loading={loading} />

        <Card className="requirements-operation__table-card" bordered={false}>
          <OpsRequestTable
            dataSource={dataSource}
            loading={loading}
            page={page}
            pageSize={pageSize}
            total={total}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPaginationChange={handlePaginationChange}
            onAssign={handleAssign}
            onView={handleView}
            onEdit={handleEdit}
            onCancel={setCancelling}
            onContinueDraft={handleContinueDraft}
            onDeleteDraft={handleDeleteDraft}
          />
        </Card>
      </div>

      <CancelRequestModal
        request={cancelling}
        onCancel={() => setCancelling(null)}
        onCancelled={() => {
          setCancelling(null);
          fetchList();
        }}
      />
    </Layout>
  );
}

export default RequirementsOperation;
